/**
 * When is the baby expected to wake from the sleep in progress?
 *
 *   expectedWakeTime(openSleep, schedule) → Date | null
 *
 * ONE definition for the home hero ("Since 20:57 · usually wakes ~07:15"), the
 * Sleep page hero and the lock-screen Live Activity target. Until 1.4.3 the
 * home hero added `schedule.avgNapMinutes` to the start whatever the category,
 * so a toddler put down for the NIGHT at 20:57 read "usually wakes ~23:17"
 * (bug 4). A nap and a night sleep answer the question differently:
 *
 *   nap    start + avgNapMinutes            (the usual nap length)
 *   night  the baby's usual MORNING wake    (`schedule.usualWakeTime`, "HH:MM"
 *          local — the server's mean of the last two weeks' morning wakes),
 *          on the start day when that clock time is at least an hour after
 *          the start, otherwise the following day; failing that
 *          start + avgNightMinutes — but only for a sleep that began at a
 *          bedtime hour (16:00–01:59 device-local): a night piece resumed at
 *          02:30 after a feed is not a whole night, and "usually wakes ~13:10"
 *          would be nonsense — failing that nothing.
 *
 * The server formats `usualWakeTime` in the zone the schedule query asked for
 * (the device zone — see useSleepSchedule), so building the Date in device
 * local time is consistent, and constructing it from wall-clock parts keeps
 * it right across a DST change during the night.
 *
 * Null means "no expected wake" — callers drop the suffix, and the Live
 * Activity driver sends target == start, its agreed "none" signal.
 */

const MS_PER_MIN = 60000;
const MIN_NIGHT_GAP_MS = 60 * MS_PER_MIN;
const CLOCK_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const toMs = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return Date.parse(value);
};

export function expectedWakeTime(openSleep, schedule) {
  const start = toMs(openSleep?.startTime);
  if (!Number.isFinite(start)) return null;

  if (openSleep?.data?.category !== 'night') {
    const avgNap = Number(schedule?.avgNapMinutes) || 0;
    return avgNap > 0 ? new Date(start + avgNap * MS_PER_MIN) : null;
  }

  const usual = typeof schedule?.usualWakeTime === 'string' ? CLOCK_RE.exec(schedule.usualWakeTime) : null;
  if (usual) {
    const hours = Number(usual[1]);
    const minutes = Number(usual[2]);
    const startDate = new Date(start);
    const sameDay = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      hours,
      minutes,
      0,
      0
    );
    if (sameDay.getTime() - start >= MIN_NIGHT_GAP_MS) return sameDay;
    return new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate() + 1,
      hours,
      minutes,
      0,
      0
    );
  }

  const avgNight = Number(schedule?.avgNightMinutes) || 0;
  if (avgNight <= 0 || !startedAtBedtimeHour(new Date(start))) return null;
  return new Date(start + avgNight * MS_PER_MIN);
}

/** 16:00–01:59 device-local — the hours a night sleep genuinely begins at. */
function startedAtBedtimeHour(date) {
  const hour = date.getHours();
  return hour >= 16 || hour < 2;
}
